import { useState, useEffect } from 'react'
import {
  MdPerson,
  MdEdit,
  MdArrowBack,
  MdBusiness,
  MdFlight,
  MdSchedule,
  MdPhone,
  MdEmail,
  MdWarning,
  MdCheckCircle,
  MdLanguage,
  MdStar,
  MdMedicalServices,
  MdLocalShipping,
  MdCalendarToday,
  MdAccessTime,
  MdSchool,
  MdSecurity,
} from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, LoadingSpinner, InfoCard, Badge } from '../../components/ui'
import { useFlightAttendant } from '../../hooks/useFlightAttendant'
import { useWebsocket } from '../../hooks/useWebsocket'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function FlightAttendantDetailPage() {
  const { flightAttendantId } = useParams()
  const navigate = useNavigate()
  const [flightAttendant, setFlightAttendant] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const dispatch = useDispatch()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()

  const { getFlightAttendantByIdAsync, isGetFlightAttendantByIdLoading } = useFlightAttendant()

  const loadFlightAttendant = async () => {
    try {
      setIsLoading(true)
      const data = await getFlightAttendantByIdAsync(flightAttendantId)
      setFlightAttendant(data)
    } catch (error) {
      console.error('Error loading flight attendant:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (flightAttendantId) {
      loadFlightAttendant()
    }
  }, [flightAttendantId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (flightAttendantId) {
      loadFlightAttendant()

      const channelName = getWebsocketChannel(flightAttendantId).flightCrewDetails

      if (channelName) {
        subscribeToChannel(channelName)
        dispatch(addSubscription(channelName))
      }

      return () => {
        if (channelName) {
          unsubscribeFromChannel(channelName)
          dispatch(removeSubscription(channelName))
        }
      }
    }
  }, [flightAttendantId]) // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // const formatDateTime = (dateString) => {
  //   if (!dateString) return 'N/A'
  //   return new Date(dateString).toLocaleString('en-US', {
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric',
  //     hour: '2-digit',
  //     minute: '2-digit',
  //   })
  // }

  const formatDateOfBirth = (dateString) => {
    if (!dateString) return 'N/A'
    const birthDate = new Date(dateString)
    const today = new Date()
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return `${age - 1} years old (${formatDate(dateString)})`
    }
    return `${age} years old (${formatDate(dateString)})`
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'inactive':
        return 'error'
      case 'on_leave':
        return 'warning'
      case 'terminated':
        return 'neutral'
      default:
        return 'neutral'
    }
  }

  // const getAvailabilityDays = (availability) => {
  //   if (!availability) return []
  //   return Object.entries(availability)
  //     .filter(([_, isAvailable]) => isAvailable)
  //     .map(([day, _]) => day.charAt(0).toUpperCase() + day.slice(1))
  // }

  const isCertificationExpiring = (dateString, daysThreshold = 30) => {
    if (!dateString) return false
    const certDate = new Date(dateString)
    const thresholdDate = new Date(Date.now() + daysThreshold * 24 * 60 * 60 * 1000)
    return certDate <= thresholdDate
  }

  if (isLoading || isGetFlightAttendantByIdLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!flightAttendant) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
            Flight Attendant Not Found
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            The flight attendant you're looking for doesn't exist or has been removed.
          </p>
          <Button variant="primary" onClick={() => navigate('/flight-attendants')}>
            Back to Flight Attendants
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4 text-left">
          <Button
            variant="ghost"
            onClick={() => navigate('/flight-attendants')}
            className="flex items-center space-x-2"
          >
            <MdArrowBack className="text-lg" />
            <span>Back</span>
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                {flightAttendant.firstName} {flightAttendant.lastName}
              </h1>
              <p className="text-neutral-600 dark:text-neutral-400 text-left">
                {flightAttendant.employeeNumber}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Badge variant={getStatusColor(flightAttendant.status)}>
              {flightAttendant.status?.charAt(0).toUpperCase() + flightAttendant.status?.slice(1)}
            </Badge>
            <Button
              variant="primary"
              onClick={() => navigate(`/flight-attendants/${flightAttendantId}/edit`)}
              className="flex items-center space-x-2"
            >
              <MdEdit className="text-lg" />
              <span>Edit</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <InfoCard title="Personal Information" icon={MdPerson}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Full Name</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {flightAttendant.firstName} {flightAttendant.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Date of Birth</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {formatDateOfBirth(flightAttendant.dateOfBirth)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Employee Number</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {flightAttendant.employeeNumber}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Date of Hire</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {formatDate(flightAttendant.dateOfHire)}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Email</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {flightAttendant.email}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Phone</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {flightAttendant.phone}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Base Airport</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {flightAttendant.baseAirport}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Seniority Number</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    #{flightAttendant.seniorityNumber}
                  </span>
                </div>
              </div>
            </div>
          </InfoCard>

          {/* Airline & Aircraft Information */}
          <InfoCard title="Airline & Aircraft Information" icon={MdBusiness}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="font-semibold text-neutral-900 dark:text-white flex items-center space-x-2">
                  <MdBusiness className="text-lg" />
                  <span>Airline</span>
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Name</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {flightAttendant.airline?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Code</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {flightAttendant.airline?.code}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Country</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {flightAttendant.airline?.country}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-neutral-900 dark:text-white flex items-center space-x-2">
                  <MdFlight className="text-lg" />
                  <span>Current Aircraft</span>
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Name</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {flightAttendant.aircraft?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Code</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {flightAttendant.aircraft?.code}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Type</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {flightAttendant.aircraft?.type}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </InfoCard>

          {/* Qualifications */}
          <InfoCard title="Qualifications" icon={MdSchool}>
            <div className="space-y-6">
              {/* Languages */}
              <div>
                <h4 className="font-semibold text-neutral-900 dark:text-white mb-3 flex items-center space-x-2">
                  <MdLanguage className="text-lg" />
                  <span>Languages</span>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {flightAttendant.qualifications?.languages?.map((lang, index) => (
                    <Badge key={lang._id || index} variant="info">
                      {lang.language} ({lang.proficiency})
                    </Badge>
                  )) || <span className="text-neutral-500">No languages specified</span>}
                </div>
              </div>

              {/* Aircraft Types */}
              <div>
                <h4 className="font-semibold text-neutral-900 dark:text-white mb-3 flex items-center space-x-2">
                  <MdFlight className="text-lg" />
                  <span>Aircraft Types</span>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {flightAttendant.qualifications?.aircraftTypes?.map((type, index) => (
                    <Badge key={type._id || index} variant="success">
                      {type.type} ({formatDate(type.certificationDate)})
                    </Badge>
                  )) || <span className="text-neutral-500">No aircraft types specified</span>}
                </div>
              </div>

              {/* Special Qualifications */}
              <div>
                <h4 className="font-semibold text-neutral-900 dark:text-white mb-3 flex items-center space-x-2">
                  <MdStar className="text-lg" />
                  <span>Special Qualifications</span>
                </h4>
                <div className="space-y-2">
                  {flightAttendant.qualifications?.specialQualifications?.map((qual, index) => (
                    <div
                      key={qual._id || index}
                      className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-neutral-900 dark:text-white">
                            {qual.type}
                          </p>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400">
                            {qual.description}
                          </p>
                        </div>
                        <Badge
                          variant={isCertificationExpiring(qual.expiryDate) ? 'warning' : 'success'}
                        >
                          {formatDate(qual.expiryDate)}
                        </Badge>
                      </div>
                    </div>
                  )) || <span className="text-neutral-500">No special qualifications</span>}
                </div>
              </div>
            </div>
          </InfoCard>

          {/* Schedule & Availability */}
          <InfoCard title="Schedule & Availability" icon={MdSchedule}>
            <div className="space-y-6">
              {/* Availability */}
              <div>
                <h4 className="font-semibold text-neutral-900 dark:text-white mb-3 flex items-center space-x-2">
                  <MdAccessTime className="text-lg" />
                  <span>Weekly Availability</span>
                </h4>
                <div className="grid grid-cols-7 gap-2">
                  {[
                    'monday',
                    'tuesday',
                    'wednesday',
                    'thursday',
                    'friday',
                    'saturday',
                    'sunday',
                  ].map((day) => (
                    <div key={day} className="text-center">
                      <div
                        className={`p-2 rounded-lg text-xs font-medium ${
                          flightAttendant.schedule?.availability?.[day]
                            ? 'bg-success-100 dark:bg-success-900 text-success-700 dark:text-success-300'
                            : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500'
                        }`}
                      >
                        {day.charAt(0).toUpperCase()}
                      </div>
                      <p className="text-xs text-neutral-500 mt-1">
                        {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preferred Routes */}
              <div>
                <h4 className="font-semibold text-neutral-900 dark:text-white mb-3 flex items-center space-x-2">
                  <MdFlight className="text-lg" />
                  <span>Preferred Routes</span>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {flightAttendant.schedule?.preferredRoutes?.map((route, index) => (
                    <Badge key={index} variant="info">
                      {route}
                    </Badge>
                  )) || <span className="text-neutral-500">No preferred routes specified</span>}
                </div>
              </div>

              {/* Time Off Requests */}
              <div>
                <h4 className="font-semibold text-neutral-900 dark:text-white mb-3 flex items-center space-x-2">
                  <MdCalendarToday className="text-lg" />
                  <span>Time Off Requests</span>
                </h4>
                <div className="space-y-2">
                  {flightAttendant.schedule?.timeOffRequests?.map((request, index) => (
                    <div
                      key={request._id || index}
                      className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-neutral-900 dark:text-white">
                            {request.type}
                          </p>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400">
                            {request.reason}
                          </p>
                          <p className="text-xs text-neutral-500">
                            {formatDate(request.startDate)} - {formatDate(request.endDate)}
                          </p>
                        </div>
                        <Badge variant={request.status === 'Approved' ? 'success' : 'warning'}>
                          {request.status}
                        </Badge>
                      </div>
                    </div>
                  )) || <span className="text-neutral-500">No time off requests</span>}
                </div>
              </div>
            </div>
          </InfoCard>

          {/* Performance */}
          <InfoCard title="Performance" icon={MdStar}>
            <div className="space-y-6">
              {/* Evaluations */}
              <div>
                <h4 className="font-semibold text-neutral-900 dark:text-white mb-3 flex items-center space-x-2">
                  <MdStar className="text-lg" />
                  <span>Recent Evaluations</span>
                </h4>
                <div className="space-y-2">
                  {flightAttendant.performance?.evaluations
                    ?.slice(0, 3)
                    .map((evaluation, index) => (
                      <div
                        key={evaluation._id || index}
                        className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-neutral-900 dark:text-white">
                              {evaluation.evaluator} - {formatDate(evaluation.date)}
                            </p>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400">
                              {evaluation.comments}
                            </p>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className="text-lg font-bold text-neutral-900 dark:text-white">
                              {evaluation.rating}
                            </span>
                            <MdStar className="text-yellow-500" />
                          </div>
                        </div>
                        {evaluation.areasOfImprovement?.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-neutral-500 mb-1">Areas for improvement:</p>
                            <div className="flex flex-wrap gap-1">
                              {evaluation.areasOfImprovement.map((area, idx) => (
                                <Badge key={idx} variant="warning" size="sm">
                                  {area}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )) || <span className="text-neutral-500">No evaluations available</span>}
                </div>
              </div>

              {/* Commendations */}
              <div>
                <h4 className="font-semibold text-neutral-900 dark:text-white mb-3 flex items-center space-x-2">
                  <MdCheckCircle className="text-lg" />
                  <span>Commendations</span>
                </h4>
                <div className="space-y-2">
                  {flightAttendant.performance?.commendations?.slice(0, 3).map((comm, index) => (
                    <div
                      key={comm._id || index}
                      className="p-3 bg-success-50 dark:bg-success-900/20 rounded-lg"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-neutral-900 dark:text-white">
                            {comm.type}
                          </p>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400">
                            {comm.description}
                          </p>
                          <p className="text-xs text-neutral-500">Issued by: {comm.issuedBy}</p>
                        </div>
                        <span className="text-xs text-neutral-500">{formatDate(comm.date)}</span>
                      </div>
                    </div>
                  )) || <span className="text-neutral-500">No commendations</span>}
                </div>
              </div>
            </div>
          </InfoCard>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Information */}
          <InfoCard title="Contact Information" icon={MdPhone}>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <MdEmail className="text-neutral-500" />
                <span className="text-sm text-neutral-900 dark:text-white">
                  {flightAttendant.email}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <MdPhone className="text-neutral-500" />
                <span className="text-sm text-neutral-900 dark:text-white">
                  {flightAttendant.phone}
                </span>
              </div>

              {/* Emergency Contact */}
              {flightAttendant.contactInfo?.emergencyContact && (
                <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
                  <h5 className="font-medium text-neutral-900 dark:text-white mb-2">
                    Emergency Contact
                  </h5>
                  <div className="space-y-1 text-sm">
                    <p className="text-neutral-900 dark:text-white">
                      {flightAttendant.contactInfo.emergencyContact.name}
                    </p>
                    <p className="text-neutral-600 dark:text-neutral-400">
                      {flightAttendant.contactInfo.emergencyContact.relationship}
                    </p>
                    <p className="text-neutral-600 dark:text-neutral-400">
                      {flightAttendant.contactInfo.emergencyContact.phone}
                    </p>
                  </div>
                </div>
              )}

              {/* Address */}
              {flightAttendant.contactInfo?.address && (
                <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
                  <h5 className="font-medium text-neutral-900 dark:text-white mb-2">Address</h5>
                  <div className="text-sm text-neutral-600 dark:text-neutral-400">
                    <p>{flightAttendant.contactInfo.address.street}</p>
                    <p>
                      {flightAttendant.contactInfo.address.city},{' '}
                      {flightAttendant.contactInfo.address.state}{' '}
                      {flightAttendant.contactInfo.address.zipCode}
                    </p>
                    <p>{flightAttendant.contactInfo.address.country}</p>
                  </div>
                </div>
              )}
            </div>
          </InfoCard>

          {/* Certifications */}
          <InfoCard title="Certifications" icon={MdSecurity}>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Initial Training
                </span>
                <span className="text-sm font-medium text-neutral-900 dark:text-white">
                  {formatDate(flightAttendant.certifications?.initialTraining)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Recurrent Training
                </span>
                <div className="flex items-center space-x-1">
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    {formatDate(flightAttendant.certifications?.recurrentTraining)}
                  </span>
                  {isCertificationExpiring(
                    flightAttendant.certifications?.nextRecurrentTraining,
                  ) && <MdWarning className="text-warning-500" />}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Next Recurrent
                </span>
                <span
                  className={`text-sm font-medium ${
                    isCertificationExpiring(flightAttendant.certifications?.nextRecurrentTraining)
                      ? 'text-warning-600 dark:text-warning-400'
                      : 'text-neutral-900 dark:text-white'
                  }`}
                >
                  {formatDate(flightAttendant.certifications?.nextRecurrentTraining)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Emergency Training
                </span>
                <div className="flex items-center space-x-1">
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    {formatDate(flightAttendant.certifications?.emergencyTraining)}
                  </span>
                  {isCertificationExpiring(
                    flightAttendant.certifications?.nextEmergencyTraining,
                  ) && <MdWarning className="text-warning-500" />}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Next Emergency
                </span>
                <span
                  className={`text-sm font-medium ${
                    isCertificationExpiring(flightAttendant.certifications?.nextEmergencyTraining)
                      ? 'text-warning-600 dark:text-warning-400'
                      : 'text-neutral-900 dark:text-white'
                  }`}
                >
                  {formatDate(flightAttendant.certifications?.nextEmergencyTraining)}
                </span>
              </div>
            </div>
          </InfoCard>

          {/* Medical Information */}
          <InfoCard title="Medical Information" icon={MdMedicalServices}>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Last Medical Exam
                </span>
                <span className="text-sm font-medium text-neutral-900 dark:text-white">
                  {formatDate(flightAttendant.medicalInfo?.lastMedicalExam)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Next Medical Exam
                </span>
                <div className="flex items-center space-x-1">
                  <span
                    className={`text-sm font-medium ${
                      isCertificationExpiring(flightAttendant.medicalInfo?.nextMedicalExam)
                        ? 'text-warning-600 dark:text-warning-400'
                        : 'text-neutral-900 dark:text-white'
                    }`}
                  >
                    {formatDate(flightAttendant.medicalInfo?.nextMedicalExam)}
                  </span>
                  {isCertificationExpiring(flightAttendant.medicalInfo?.nextMedicalExam) && (
                    <MdWarning className="text-warning-500" />
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">Blood Type</span>
                <span className="text-sm font-medium text-neutral-900 dark:text-white">
                  {flightAttendant.medicalInfo?.bloodType || 'N/A'}
                </span>
              </div>

              {/* Allergies */}
              {flightAttendant.medicalInfo?.allergies?.length > 0 && (
                <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
                  <h5 className="font-medium text-neutral-900 dark:text-white mb-2">Allergies</h5>
                  <div className="flex flex-wrap gap-1">
                    {flightAttendant.medicalInfo.allergies.map((allergy, index) => (
                      <Badge key={index} variant="warning" size="sm">
                        {allergy}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Medications */}
              {flightAttendant.medicalInfo?.medications?.length > 0 && (
                <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
                  <h5 className="font-medium text-neutral-900 dark:text-white mb-2">Medications</h5>
                  <div className="space-y-1">
                    {flightAttendant.medicalInfo.medications.map((med, index) => (
                      <div key={med._id || index} className="text-sm">
                        <p className="font-medium text-neutral-900 dark:text-white">{med.name}</p>
                        <p className="text-neutral-600 dark:text-neutral-400">
                          {med.dosage} - {med.frequency}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </InfoCard>

          {/* Uniform Information */}
          <InfoCard title="Uniform Information" icon={MdLocalShipping}>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Jacket</span>
                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                    {flightAttendant.uniform?.sizes?.jacket || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Pants</span>
                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                    {flightAttendant.uniform?.sizes?.pants || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Shirt</span>
                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                    {flightAttendant.uniform?.sizes?.shirt || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">Shoes</span>
                  <p className="text-sm font-medium text-neutral-900 dark:text-white">
                    {flightAttendant.uniform?.sizes?.shoes || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    Last Issued
                  </span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    {formatDate(flightAttendant.uniform?.lastIssued)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    Next Issuance
                  </span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    {formatDate(flightAttendant.uniform?.nextIssuance)}
                  </span>
                </div>
              </div>
            </div>
          </InfoCard>
        </div>
      </div>
    </div>
  )
}

export default FlightAttendantDetailPage
